import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { Freight } from '../../shared/models/sale/freight';
import { IFreightCost } from '../../shared/models/sale/freight-cost';
import { IPagedResult } from '../../shared/models/paged-result';
import { FreightFilterSet } from '../../shared/models/sale/freight-filter-set';

export const freightApi = createApi({
  reducerPath: 'freightApi',
  baseQuery,
  tagTypes: ['Freight'],
  endpoints: (builder) => ({
    getByZoneId: builder.query<Freight, string>({
      query: (zoneId) => ({
        url: `/api/fletes/zone/${zoneId}`,
        method: 'GET',
      }),
    }),
    getFreightCost: builder.query<IFreightCost, string>({
      query: (params) => ({
        url: `/api/fletes/cost?${params}`,
        method: 'GET',
      }),
    }),
    getLocalCost: builder.query<
      IFreightCost,
      { ubigeoCode: string; weight: number; companyId: string }
    >({
      query: (params) => ({
        url: `/api/fletes/local-cost?ubigeoCode=${params.ubigeoCode}&weight=${params.weight}&companyId=${params.companyId}`,
        method: 'GET',
      }),
    }),
    getFreights: builder.query<IPagedResult<Freight>, FreightFilterSet>({
      query: (params) => ({
        url: '/api/fletes',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: string }) => ({ type: 'Freight' as const, id })),
              { type: 'Freight', id: 'LIST' },
            ]
          : [{ type: 'Freight', id: 'LIST' }],
    }),
    getFreightById: builder.query<Freight, string>({
      query: (id) => `/api/fletes/${id}`,
      providesTags: (_, __, id) => [{ type: 'Freight', id }],
    }),
    addFreight: builder.mutation<Freight, Partial<Freight>>({
      query: (body) => ({
        url: '/api/fletes',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Freight', id: 'LIST' }],
    }),
    updateFreight: builder.mutation<Freight, Partial<Freight>>({
      query: (data) => {
        const { id, ...body } = data;
        return {
          url: `/api/fletes/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'Freight', id }],
    }),
    deleteFreight: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/fletes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Freight', id: 'LIST' }],
    }),
  }),
});

export const {
  useLazyGetByZoneIdQuery,
  useLazyGetFreightCostQuery,
  useLazyGetLocalCostQuery,
  useLazyGetFreightsQuery,
  useGetFreightByIdQuery,
  useAddFreightMutation,
  useUpdateFreightMutation,
  useDeleteFreightMutation,
} = freightApi;
